/** @format */
/** @format */

import React, { FC, useState } from 'react'
import styled from 'styled-components'
import {
  _$,
  FormItem,
  Icon,
  Input,
  Select,
  Switch,
  Option,
  Spinner,
  Table,
  TableColumn,
  OptionGroup,
  Tooltip
} from '@king-design/react'
import { AddButon } from 'src/components'
import {
  VisualRuleProps,
  initVisualRule,
  TriggerRule,
  initTriggerRule
} from 'src/pages/PrometheusMonitor/Alarm/constants'
import { cloneDeep } from 'lodash'
import { useAsync } from 'react-use'
import ApiCommon from 'src/api/ApiCommon'
import { request } from 'src/utils'
import {
  periodOption,
  methodOption,
  getMetricUnitOptions,
  withCustomRule,
  pointsOption,
  intervalOption,
  countOption
} from 'src/containers/MonitorCreate/AlarmRule/constants'
import VisualRuleLabels from './VisualRuleLabels'

interface VisualRuleListProps {
  className?: string
  productType: number
  ruleValue: VisualRuleProps[]
  onChangeValue: (value: VisualRuleProps[]) => void
}
const VisualRuleList: FC<VisualRuleListProps> = ({
  className,
  ruleValue,
  onChangeValue,
  productType
}) => {
  const [metricGroup, setMetricGroup] = useState([])
  // 获取指标列表
  const { value: metricList } = useAsync(async () => {
    if (productType !== undefined) {
      const { data } = await request(ApiCommon.getProductMetricList, {
        data: { ProductType: productType, PageIndex: 1 }
      })
      if (data?.metricList?.length) {
        const result = []
        data?.metricList.forEach(
          ({ application, metricKey, metricName, units, delay, isDimension }) => {
            const key = application || _$('其它')
            const findIndex = result.findIndex(({ app }) => app === key)
            if (findIndex >= 0) {
              result[findIndex].children.push({
                metricKey,
                metricName,
                units,
                delay,
                isDimension
              })
            } else {
              result.push({
                app: key,
                children: [{ metricKey, metricName, units, delay, isDimension }]
              })
            }
          }
        )
        setMetricGroup(result)
      }
      return data?.metricList || []
    }
    return []
  }, [productType])

  // 修改告警规则
  const changeRule = (value: Partial<VisualRuleProps>, ruleIndex: number) => {
    const copyRules = cloneDeep(ruleValue)
    if (value) {
      copyRules[ruleIndex] = {
        ...copyRules[ruleIndex],
        ...value
      }
    } else {
      copyRules.splice(ruleIndex, 1)
    }
    onChangeValue(copyRules)
  }

  // 修改指标规则
  const changeMetricRule = (
    value: Partial<TriggerRule>,
    ruleIndex: number,
    metricIndex: number
  ) => {
    const copyRules = cloneDeep(ruleValue)
    if (value) {
      copyRules[ruleIndex].TriggerRules[metricIndex] = {
        ...copyRules[ruleIndex].TriggerRules[metricIndex],
        ...value
      }
    } else {
      copyRules[ruleIndex].TriggerRules.splice(metricIndex, 1)
    }
    onChangeValue(copyRules)
  }

  return (
    <div className={className}>
      {productType !== undefined ? (
        <>
          {ruleValue?.map((rule, ruleIndex) => {
            const {
              RuleID,
              Alert,
              State,
              Condition,
              For,
              Severity,
              TriggerCount,
              AlertInterval,
              MaxAlertCount,
              TriggerRules
            } = rule
            return (
              <div className="visual_rule_item" key={RuleID || ruleIndex}>
                <div className="rule_header">
                  <div className="ksc-space">
                    <i style={{ marginRight: 8 }}>#{ruleIndex + 1}.</i>
                    <FormItem
                      style={{ margin: 0 }}
                      label={_$('规则名称')}
                      value={Alert}
                      rules={{
                        required: true,
                        userName: true,
                        unique: value => {
                          return ruleValue.some(
                            (item, index) => item.Alert === value && ruleIndex !== index
                          )
                            ? _$('单个策略内，规则名称不允许重复')
                            : true
                        }
                      }}
                    >
                      <Input
                        value={Alert}
                        onChangeValue={value => {
                          changeRule({ Alert: value }, ruleIndex)
                        }}
                      />
                    </FormItem>
                    <FormItem label={_$('规则启停')} value={State}>
                      <Switch
                        size="small"
                        value={State === 1}
                        onChangeValue={value => {
                          changeRule({ State: value ? 1 : 2 }, ruleIndex)
                        }}
                      />
                    </FormItem>
                  </div>
                  <div className="ksc-space">
                    {ruleValue?.length > 1 ? (
                      <Icon
                        className="ion-trash-a"
                        size={16}
                        onClick={() => {
                          changeRule(undefined, ruleIndex)
                        }}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="rule_config">
                  <p>{_$('当下面指标满足')}</p>
                  <Tooltip
                    slotContent={
                      <>
                        <div>{_$('且：所有规则都满足时触发告警')}</div>
                        <div>{_$('或：任意一个规则满足时触发告警')}</div>
                      </>
                    }
                  >
                    <Select
                      value={Condition}
                      width={90}
                      onChangeValue={value => {
                        changeRule({ Condition: value }, ruleIndex)
                      }}
                    >
                      <Option value={1}>{_$('且')}</Option>
                      <Option value={2}>{_$('或')}</Option>
                    </Select>
                  </Tooltip>
                  <p>{_$('条件且持续')}</p>
                  <Tooltip content={_$('0表示满足条件就触发')}>
                    <Spinner
                      value={For}
                      min={0}
                      precision={0}
                      step={1}
                      onChangeValue={value => {
                        changeRule({ For: value }, ruleIndex)
                      }}
                    />
                  </Tooltip>
                  <p>{_$('分钟时，将触发')}</p>
                  <Select
                    value={Severity}
                    width={120}
                    onChangeValue={value => {
                      changeRule({ Severity: value }, ruleIndex)
                    }}
                  >
                    <Option value="critical">critical</Option>
                    <Option value="error">error</Option>
                    <Option value="warning">warning</Option>
                  </Select>
                  <p>{_$('级别告警')}</p>
                </div>
                <div className="rule_content">
                  <Table
                    data={TriggerRules || []}
                    animation={false}
                    resizable={true}
                    type="grid"
                    merge={(row, column, rowIndex, columnIndex) => {
                      if (
                        column?.key === 'TriggerCount' ||
                        column?.key === 'AlertInterval' ||
                        column?.key === 'MaxAlertCount'
                      ) {
                        return {
                          rowspan: TriggerRules.length,
                          colspan: 1
                        }
                      }
                      return undefined
                    }}
                    rowKey={(data, index) => {
                      return data?.SerialNumber || index
                    }}
                    checkType="none"
                  >
                    <TableColumn fixed="left" key="SerialNumber" title={_$('序号')} width={60} />
                    <TableColumn
                      key="MetricName"
                      title={_$('指标')}
                      width={200}
                      fixed="left"
                      slotTemplate={([data, metricIndex]) => {
                        const { Metric, MetricName } = data
                        return (
                          <FormItem hideLabel={true} value={Metric} rules={{ required: true }}>
                            <Select
                              card
                              filterable
                              value={Metric && MetricName ? `${Metric}===${MetricName}` : ''}
                              width={180}
                              placeholder={_$('选择监控项')}
                              onChangeValue={value => {
                                const valueSplit = value.split('===')
                                const find = metricList?.find(
                                  item =>
                                    item.metricKey === valueSplit[0] &&
                                    item.metricName === valueSplit[1]
                                )
                                if (find) {
                                  const { metricKey, metricName, units } = find
                                  const unitOpts = getMetricUnitOptions(
                                    productType,
                                    valueSplit[0],
                                    units
                                  )
                                  let initUnit = find.units
                                  if (unitOpts?.length) {
                                    if (unitOpts.includes('Mbps')) {
                                      initUnit = 'Mbps'
                                    }
                                  } else if (find.units === 'bps') {
                                    initUnit = 'Mbps'
                                  }
                                  changeMetricRule(
                                    {
                                      Metric: metricKey,
                                      MetricName: metricName,
                                      Units: units,
                                      DisplayUnits: initUnit,
                                      UnitsOption: unitOpts || []
                                    },
                                    ruleIndex,
                                    metricIndex
                                  )
                                }
                              }}
                            >
                              {metricGroup?.length
                                ? metricGroup.map(({ app, children }) => (
                                    <OptionGroup label={app} key={app}>
                                      {children.map(item => (
                                        <Option
                                          key={`${item.metricKey}===${item.metricName}`}
                                          label={item.metricName}
                                          value={`${item.metricKey}===${item.metricName}`}
                                        >
                                          <div className="metric_option">
                                            <p>{item.metricName}</p>
                                            <p className="metric_key">{item.metricKey}</p>
                                          </div>
                                        </Option>
                                      ))}
                                    </OptionGroup>
                                  ))
                                : null}
                            </Select>
                          </FormItem>
                        )
                      }}
                    />
                    <TableColumn
                      key="Labels"
                      title={_$('维度')}
                      width={345}
                      slotTemplate={([data, metricIndex]) => {
                        const { Labels, Metric } = data
                        return (
                          <VisualRuleLabels
                            key={`${ruleIndex}-${metricIndex}`}
                            productType={productType}
                            metricKey={Metric}
                            labels={Labels}
                            onChange={value => {
                              changeMetricRule({ Labels: value }, ruleIndex, metricIndex)
                            }}
                          />
                        )
                      }}
                    />
                    <TableColumn
                      key="AggPeriod"
                      title={_$('统计周期')}
                      width={120}
                      slotTemplate={([data, metricIndex]) => {
                        return withCustomRule.includes(data?.Metric) ? null : (
                          <FormItem
                            hideLabel={true}
                            value={data?.AggPeriod}
                            rules={{ required: true }}
                          >
                            <Select
                              value={data?.AggPeriod}
                              width={100}
                              onChangeValue={value => {
                                changeMetricRule({ AggPeriod: value }, ruleIndex, metricIndex)
                              }}
                            >
                              {periodOption.map(item => (
                                <Option key={item.value} value={item.value}>
                                  {_$(item.title)}
                                </Option>
                              ))}
                            </Select>
                          </FormItem>
                        )
                      }}
                    />
                    <TableColumn
                      key="AggMethod"
                      title={_$('统计方法')}
                      width={120}
                      slotTemplate={([data, metricIndex]) => {
                        return withCustomRule.includes(data?.Metric) ? null : (
                          <FormItem
                            hideLabel={true}
                            value={data?.AggMethod}
                            rules={{ required: true }}
                          >
                            <Select
                              value={data?.AggMethod}
                              width={100}
                              onChangeValue={value => {
                                changeMetricRule({ AggMethod: value }, ruleIndex, metricIndex)
                              }}
                            >
                              {methodOption.map(item => (
                                <Option key={item.value} value={item.value}>
                                  {_$(item.title)}
                                </Option>
                              ))}
                            </Select>
                          </FormItem>
                        )
                      }}
                    />
                    <TableColumn
                      key="Operator"
                      title={_$('大小')}
                      width={100}
                      slotTemplate={([data, metricIndex]) => {
                        const { Metric } = data
                        return withCustomRule.includes(Metric) ||
                          Metric?.includes('.nodata') ? null : (
                          <FormItem
                            hideLabel={true}
                            value={data?.Operator}
                            rules={{ required: true }}
                          >
                            <Select
                              value={data?.Operator}
                              width={80}
                              onChangeValue={value => {
                                changeMetricRule({ Operator: value }, ruleIndex, metricIndex)
                              }}
                            >
                              <Option value=">">{'>'}</Option>
                              <Option value="<">{'<'}</Option>
                              <Option value="==">{'=='}</Option>
                              <Option value="!=">{'!='}</Option>
                              <Option value=">=">{'>='}</Option>
                              <Option value="<=">{'<='}</Option>
                            </Select>
                          </FormItem>
                        )
                      }}
                    />
                    <TableColumn
                      key="Threshold"
                      title={_$('阈值')}
                      width={200}
                      slotTemplate={([data, metricIndex]) => {
                        const { Metric, Units, DisplayUnits, Threshold, UnitsOption = [] } = data
                        return withCustomRule.includes(data?.Metric) ||
                          Metric?.includes('.nodata') ? null : (
                          <FormItem
                            className="table-space"
                            hideLabel={true}
                            value={Threshold}
                            rules={{ required: true, pointsLimit: true }}
                          >
                            <Input
                              value={Threshold}
                              width={180}
                              onChangeValue={value => {
                                changeMetricRule({ Threshold: value }, ruleIndex, metricIndex)
                              }}
                              slotAppend={
                                UnitsOption?.length ? (
                                  <Select
                                    width={90}
                                    value={DisplayUnits || Units || ''}
                                    onChangeValue={value => {
                                      changeMetricRule(
                                        { DisplayUnits: value },
                                        ruleIndex,
                                        metricIndex
                                      )
                                    }}
                                  >
                                    {UnitsOption.map(item => (
                                      <Option key={item} value={item}>
                                        {item}
                                      </Option>
                                    ))}
                                  </Select>
                                ) : (
                                  DisplayUnits || Units || undefined
                                )
                              }
                            />
                          </FormItem>
                        )
                      }}
                    />
                    <TableColumn
                      key="TriggerCount"
                      title={_$('持续周期数')}
                      width={120}
                      slotTemplate={([data]) => {
                        const { Metric } = data
                        return withCustomRule.includes(Metric) ||
                          Metric?.includes('.nodata') ? null : (
                          <FormItem
                            hideLabel={true}
                            value={TriggerCount}
                            rules={{ required: true }}
                          >
                            <Select
                              width={100}
                              value={TriggerCount}
                              onChangeValue={value => {
                                changeRule({ TriggerCount: value as number }, ruleIndex)
                              }}
                            >
                              {pointsOption.map(item => (
                                <Option key={item.value} value={item.value}>
                                  {_$(item.title)}
                                </Option>
                              ))}
                            </Select>
                          </FormItem>
                        )
                      }}
                    />
                    <TableColumn
                      key="AlertInterval"
                      title={_$('告警间隔')}
                      width={120}
                      slotTemplate={([data]) => {
                        return (
                          <FormItem
                            hideLabel={true}
                            value={AlertInterval}
                            rules={{ required: true }}
                          >
                            <Select
                              width={100}
                              value={AlertInterval}
                              onChangeValue={value => {
                                changeRule({ AlertInterval: value as number }, ruleIndex)
                              }}
                            >
                              {intervalOption
                                .filter(item => item.value >= 60)
                                .map(item => (
                                  <Option key={item.value} value={item.value}>
                                    {_$(item.title)}
                                  </Option>
                                ))}
                            </Select>
                          </FormItem>
                        )
                      }}
                    />
                    <TableColumn
                      key="MaxAlertCount"
                      title={_$('最大告警次数')}
                      width={120}
                      slotTemplate={([data]) => {
                        return (
                          <FormItem
                            hideLabel={true}
                            value={MaxAlertCount}
                            rules={{ required: true }}
                          >
                            <Select
                              width={100}
                              value={MaxAlertCount}
                              onChangeValue={value => {
                                changeRule({ MaxAlertCount: value as number }, ruleIndex)
                              }}
                            >
                              {countOption.map(item => (
                                <Option key={item.value} value={item.value}>
                                  {_$(item.title)}
                                </Option>
                              ))}
                            </Select>
                          </FormItem>
                        )
                      }}
                    />
                    <TableColumn
                      key="operate"
                      width={80}
                      title={_$('操作')}
                      fixed="right"
                      slotTemplate={([data, metricIndex]) => {
                        return TriggerRules.length > 1 ? (
                          <a
                            onClick={() => {
                              changeMetricRule(undefined, ruleIndex, metricIndex)
                            }}
                          >
                            {_$('删除')}
                          </a>
                        ) : null
                      }}
                    />
                  </Table>
                  <AddButon
                    text={_$('新建指标')}
                    disabled={TriggerRules?.length >= 5}
                    onClick={() => {
                      changeMetricRule(
                        { ...initTriggerRule, SerialNumber: TriggerRules.length + 1 },
                        ruleIndex,
                        TriggerRules.length
                      )
                    }}
                  />
                </div>
              </div>
            )
          })}
          <AddButon
            text={_$('新建规则')}
            disabled={ruleValue?.length >= 10}
            onClick={() => {
              onChangeValue(ruleValue.concat(initVisualRule))
            }}
          />
        </>
      ) : (
        <span style={{ color: '#999' }}>{_$('请选择产品类型')}</span>
      )}
    </div>
  )
}

export default styled(VisualRuleList)`
  .visual_rule_item {
    width: 100%;
    padding: 16px 16px 32px 16px;
    border: 1px solid #f3f5f6;
    background-color: #fafafa;
    margin-bottom: 16px;
    .rule_header {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }
    .rule_config {
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      & > p {
        margin: 0 4px;
        span {
          color: #999;
        }
      }
    }
    .rule_content {
      .k-table {
        margin-bottom: 16px;
        th {
          &::before {
            display: none;
          }
        }
      }
      .k-form-error {
        position: static;
      }
    }
  }
`
